// The quality check card: an accordion whose body depends on the check's
// Odoo test type. Sign-off controls stay inert until the instructions have
// been read — the spec's rule, and the reason the instruction pane scrolls.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import DOMPurify from 'dompurify';
import type { CheckVM } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import type { CheckAction } from '@/queries';

/** Long instructions must be scrolled; short ones are read at a glance. */
const AUTO_READ_CHARS = 520;

export interface CheckCardProps {
  check: CheckVM;
  index: number;
  open: boolean;
  read: boolean;
  pending: boolean;
  size?: 'phone' | 'board';
  onToggle: () => void;
  onRead: () => void;
  onAction: (a: CheckAction) => void;
  onCapturePhoto: () => void;
  /** A just-captured frame (bare base64) awaiting Retake or Confirm. */
  pendingPhoto?: string | undefined;
  onConfirmPhoto?: (() => void) | undefined;
}

export function CheckCard({
  check,
  index,
  open,
  read,
  pending,
  size = 'phone',
  onToggle,
  onRead,
  onAction,
  onCapturePhoto,
  pendingPhoto,
  onConfirmPhoto,
}: CheckCardProps) {
  const t = useT();
  const board = size === 'board';
  const resolved = check.state === 'pass' || check.state === 'fail';
  const failed = check.state === 'fail';

  const borderColour = failed
    ? 'var(--danger)'
    : check.state === 'pass'
      ? 'var(--pos)'
      : open
        ? 'var(--accent)'
        : 'var(--line)';

  const icon = resolved ? (failed ? 'cross' : 'check') : widgetIcon(check.widget);

  return (
    <li
      // Addressable, so the job's blocked Done button can bring the first
      // failed check into view instead of leaving the operator to find it.
      id={`check-${check.id}`}
      style={{
        listStyle: 'none',
        background: 'var(--surface)',
        borderRadius: board ? 14 : 15,
        border: `1.5px solid ${borderColour}`,
        boxShadow: 'var(--elev)',
        overflow: 'hidden',
      }}
    >
      <h3 style={{ margin: 0 }}>
        <button
          type="button"
          className="press-row"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`check-body-${check.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            width: '100%',
            padding: board ? '15px 16px' : '14px 15px',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: board ? 28 : 26,
              height: board ? 28 : 26,
              borderRadius: 8,
              flex: 'none',
              display: 'grid',
              placeItems: 'center',
              background: resolved ? (failed ? 'var(--danger)' : 'var(--pos)') : 'var(--surface2)',
              color: resolved ? '#fff' : 'var(--sub)',
            }}
          >
            <Icon name={icon} size={15} />
          </span>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <span style={{ display: 'block', font: '600 13px Manrope,sans-serif', color: 'var(--ink)' }}>
              {index + 1}. {check.title}
            </span>
            <span style={{ display: 'block', font: '500 10px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
              {hintFor(check, t)}
            </span>
          </span>
          {resolved ? (
            <span
              role="button"
              tabIndex={0}
              className="press"
              onClick={(e) => {
                e.stopPropagation();
                onAction({ kind: 'undo' });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onAction({ kind: 'undo' });
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '9px 11px',
                borderRadius: 10,
                background: 'var(--surface2)',
                color: 'var(--sub)',
                font: '600 11px Manrope,sans-serif',
                flex: 'none',
              }}
            >
              <Icon name="undo" size={13} /> {t.undo}
            </span>
          ) : (
            <span
              aria-hidden="true"
              style={{
                color: 'var(--sub)',
                transform: open ? 'rotate(90deg)' : 'none',
                transition: 'transform .15s',
                flex: 'none',
              }}
            >
              ▸
            </span>
          )}
        </button>
      </h3>

      {open && (
        <div id={`check-body-${check.id}`} role="region" style={{ padding: board ? '0 16px 16px' : '0 15px 15px' }}>
          <Instructions html={check.instructionsHtml} length={check.instructionsLength} read={read} onRead={onRead} board={board} />
          {!read && (
            <p role="status" style={{ font: '600 11px Manrope,sans-serif', color: 'var(--warn)', textAlign: 'center', margin: '10px 0 0' }}>
              {t.scrollToSignoff}
            </p>
          )}
          <div style={{ marginTop: 12, opacity: read ? 1 : 0.45, pointerEvents: read ? 'auto' : 'none' }}>
            <CheckBody
              check={check}
              board={board}
              pending={pending}
              onAction={onAction}
              onCapturePhoto={onCapturePhoto}
              pendingPhoto={pendingPhoto}
              onConfirmPhoto={onConfirmPhoto}
            />
          </div>
          {failed && check.failureMessage && (
            <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--danger)', margin: '10px 0 0' }}>
              {check.failureMessage}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function widgetIcon(w: CheckVM['widget']): 'camera' | 'ruler' | 'list' {
  if (w === 'photo') return 'camera';
  if (w === 'measure' || w === 'value') return 'ruler';
  return 'list';
}

function hintFor(check: CheckVM, t: ReturnType<typeof useT>): string {
  // A failed check holds the work order's sign-off (interpod_quality refuses
  // button_finish on it), and the way out is Pass once it is put right.
  if (check.state === 'fail') return t.checkFailedHintT;
  switch (check.widget) {
    case 'passfail':
      return `${t.pass} / ${t.fail}`;
    case 'measure': {
      const max = check.measure?.max;
      const unit = check.measure?.unit ?? '';
      return max != null ? `${t.record} · ≤ ${max} ${unit}`.trim() : t.record;
    }
    case 'photo':
      return t.takePhoto;
    case 'selection':
      return t.chooseOne;
    case 'mould':
      return t.chooseMouldT;
    case 'value':
      return t.enterValue;
    case 'confirm':
      return t.confirmChecked;
    default:
      return t.checkRecordedInOdoo;
  }
}

/** Scrollable instruction pane; reaching the end satisfies the sign-off gate. */
function Instructions({
  html,
  length,
  read,
  onRead,
  board,
}: {
  html: string;
  length: number;
  read: boolean;
  onRead: () => void;
  board: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const t = useT();

  useEffect(() => {
    // Short instructions cannot be scrolled, so they count as read on open.
    if (!read && length <= AUTO_READ_CHARS) onRead();
  }, [read, length, onRead]);

  useEffect(() => {
    const el = ref.current;
    if (!el || read) return;
    // Nothing to scroll (short text in a tall pane) — treat as read.
    if (el.scrollHeight <= el.clientHeight + 4) onRead();
  }, [read, onRead, html]);

  const clean = DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'span', 'div', 'h4', 'h5'],
    ALLOWED_ATTR: [],
  });

  return (
    <>
      <div className="sectionLabel" style={{ marginBottom: 6 }}>
        {t.instructionsRead}
      </div>
      <div
        ref={ref}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) onRead();
        }}
        style={{
          maxHeight: board ? 170 : 150,
          overflowY: 'auto',
          background: 'var(--surface2)',
          borderRadius: 11,
          padding: 12,
          font: '500 12.5px/1.55 Manrope,sans-serif',
          color: 'var(--ink)',
        }}
        // Sanitized above; Odoo's quality.point.note is authored HTML.
        dangerouslySetInnerHTML={{ __html: clean || `<p style="opacity:.6">${t.noDocuments}</p>` }}
      />
    </>
  );
}

function CheckBody({
  check,
  board,
  pending,
  onAction,
  onCapturePhoto,
  pendingPhoto,
  onConfirmPhoto,
}: {
  check: CheckVM;
  board: boolean;
  pending: boolean;
  onAction: (a: CheckAction) => void;
  onCapturePhoto: () => void;
  /** A just-captured frame (bare base64) awaiting Retake or Confirm. */
  pendingPhoto?: string | undefined;
  onConfirmPhoto?: (() => void) | undefined;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const size = board ? 'md' : 'md';
  const row: CSSProperties = { display: 'flex', gap: 10 };

  // Pass is green and Fail is red before anything is tapped, so the verdict
  // reads from across the bay; once one is taken the other steps back.
  const passFail = (
    <div style={row}>
      <Button
        variant="pos"
        size={size}
        pending={pending}
        onClick={() => onAction({ kind: 'pass' })}
        style={check.state === 'fail' ? { opacity: 0.4 } : undefined}
      >
        {check.widget === 'passfail' && check.testTypeId === 17 ? 'Yes' : t.pass}
      </Button>
      <Button
        variant="danger"
        size={size}
        pending={pending}
        onClick={() => onAction({ kind: 'fail' })}
        style={check.state === 'pass' ? { opacity: 0.4 } : undefined}
      >
        {check.widget === 'passfail' && check.testTypeId === 17 ? 'No' : t.fail}
      </Button>
    </div>
  );

  switch (check.widget) {
    case 'passfail':
      return passFail;

    case 'confirm':
      return (
        <Button
          variant={check.state === 'pass' ? 'pos' : 'primary'}
          size={size}
          pending={pending}
          onClick={() => onAction({ kind: 'pass' })}
        >
          {check.state === 'pass' ? `✓ ${t.confirmed}` : t.confirmChecked}
        </Button>
      );

    case 'measure': {
      const m = check.measure;
      const value = Number(draft);
      const valid = draft !== '' && Number.isFinite(value);
      const over = m?.max != null && valid && value > m.max;
      return (
        <div>
          <div style={{ ...row, alignItems: 'center' }}>
            <input
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label={check.title}
              style={{
                flex: 1,
                height: 46,
                borderRadius: 11,
                background: 'var(--surface2)',
                border: '1.5px solid var(--line)',
                padding: '0 12px',
                font: "700 17px 'Space Grotesk',sans-serif",
                color: 'var(--ink)',
              }}
            />
            {m?.unit && <span style={{ font: '600 13px Manrope,sans-serif', color: 'var(--sub)' }}>{m.unit}</span>}
            <Button
              variant="primary"
              size={size}
              full={false}
              disabled={!valid}
              pending={pending}
              onClick={() => onAction({ kind: 'measure', value })}
              style={{ paddingInline: 18 }}
            >
              {t.record}
            </Button>
          </div>
          {!!m?.presets.length && (
            <>
              <p style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', margin: '10px 0 6px' }}>
                {t.tapCommon}
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {m.presets.map((p) => {
                  const bad = m.max != null && p > m.max;
                  return (
                    <button
                      key={p}
                      type="button"
                      className="press"
                      onClick={() => setDraft(String(p))}
                      style={{
                        flex: 1,
                        minWidth: 56,
                        height: board ? 36 : 42,
                        borderRadius: 11,
                        background: draft === String(p) ? (bad ? 'var(--danger)' : 'var(--pos)') : 'var(--surface2)',
                        color: draft === String(p) ? '#fff' : 'var(--ink)',
                        font: "600 13px 'Space Grotesk',sans-serif",
                      }}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {valid && (
            <p
              style={{
                font: '600 11.5px Manrope,sans-serif',
                color: over ? 'var(--danger)' : 'var(--pos)',
                margin: '10px 0 0',
              }}
            >
              {over
                ? `⚠ ${value} ${m?.unit ?? ''} — over tolerance, raise a quality alert`
                : `✓ ${value} ${m?.unit ?? ''} — within tolerance`}
            </p>
          )}
        </div>
      );
    }

    case 'photo':
      // A captured frame is shown back before it is written. On a shop floor a
      // photo is as likely to be of a thumb or a floor as of the thing being
      // recorded, and once it is in Odoo it is the evidence.
      if (pendingPhoto) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <img
              src={`data:image/jpeg;base64,${pendingPhoto}`}
              alt="The photo you just took, before it is saved"
              style={{
                width: '100%',
                maxHeight: board ? 320 : 220,
                objectFit: 'contain',
                borderRadius: 12,
                background: 'var(--surface2)',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="surface" size={size} full={false} onClick={onCapturePhoto} icon={<Icon name="camera" size={16} />}>
                {t.retakePhoto}
              </Button>
              <Button variant="primary" size={size} pending={pending} onClick={onConfirmPhoto}>
                {t.confirmPhoto}
              </Button>
            </div>
          </div>
        );
      }
      return (
        <div>
          <Button
            variant={check.hasPhoto ? 'surface' : 'primary'}
            size={size}
            pending={pending}
            icon={<Icon name="camera" size={17} />}
            onClick={onCapturePhoto}
            style={check.hasPhoto ? undefined : { borderStyle: 'dashed' }}
          >
            {check.hasPhoto ? `✓ ${t.photoCaptured} — ${t.retake}` : t.tapCapture}
          </Button>
        </div>
      );

    case 'selection':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(check.options ?? []).length === 0 ? (
            // The point has no option set (or only archived options). Nothing
            // the operator does here can fix that, so say who can.
            <p style={{ font: '500 12px Manrope,sans-serif', color: 'var(--warn)', margin: 0 }}>{t.noOptionsConfiguredT}</p>
          ) : (
            (check.options ?? []).map((o) => (
              <Button
                key={o.id}
                variant={check.selectedOptionId === o.id ? 'primary' : 'surface'}
                size={size}
                pending={pending}
                onClick={() => onAction({ kind: 'select', optionId: o.id })}
              >
                {o.name}
              </Button>
            ))
          )}
        </div>
      );

    case 'mould':
      // Which registry mould cast this pod. The eligible list is Odoo's
      // (allowed_mould_ids, per pod variant); an empty list is blocking by
      // design — the mould has to be registered, not guessed — so there is no
      // Pass to tap and no Fail either: a mould check has nothing to fail.
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(check.moulds ?? []).length === 0 ? (
            <p style={{ font: '500 12px/1.45 Manrope,sans-serif', color: 'var(--warn)', margin: 0 }}>{t.noMouldRegisteredT}</p>
          ) : (
            (check.moulds ?? []).map((m) => (
              <Button
                key={m.id}
                variant={check.mouldId === m.id ? 'primary' : 'surface'}
                size={size}
                pending={pending}
                onClick={() => onAction({ kind: 'mould', mouldId: m.id })}
              >
                {m.name}
              </Button>
            ))
          )}
        </div>
      );

    case 'value':
      return (
        <div style={{ ...row, alignItems: 'center' }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label={check.title}
            placeholder={t.enterValue}
            style={{
              flex: 1,
              height: 46,
              borderRadius: 11,
              background: 'var(--surface2)',
              border: '1.5px solid var(--line)',
              padding: '0 12px',
              font: '600 14px Manrope,sans-serif',
              color: 'var(--ink)',
            }}
          />
          <Button
            variant="primary"
            size={size}
            full={false}
            disabled={!draft.trim()}
            pending={pending}
            onClick={() => onAction({ kind: 'value', text: draft.trim() })}
            style={{ paddingInline: 18 }}
          >
            {t.record}
          </Button>
        </div>
      );

    default:
      // A test type with no designed body. Say so plainly rather than
      // pretending to handle it; the operator can still record the outcome.
      return (
        <div>
          <p style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', margin: '0 0 10px' }}>
            {t.checkRecordedInOdoo}
          </p>
          {passFail}
        </div>
      );
  }
}
